import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  items: [
    { fa: "", en: "" },
    { fa: "", en: "" },
  ],
  layout: "top-bottom",
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";

  const body = item.body || {};
  const rawItems = Array.isArray(body.items) ? body.items : [];

  const items =
    rawItems.length > 0
      ? rawItems.map((it) => ({
          fa: it?.label?.fa || "",
          en: it?.label?.en || "",
        }))
      : makeEmptyForm().items;

  const layout = body.layout || "top-bottom";
  const points = item?.points ?? 1;
  const status = item?.status || "published";
  const tags = Array.isArray(item?.tags) ? item.tags.join(",") : "";
  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    items,
    layout,
    points,
    status,
    tags,
    explanation_fa,
    explanation_en,
  };
};

export default function OrderingModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, isEdit, item]);

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const addItem = () =>
    setForm((f) => ({ ...f, items: [...f.items, { fa: "", en: "" }] }));
  const removeItem = (idx) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= form.items.length) return;
    const next = [...form.items];
    const [it] = next.splice(idx, 1);
    next.splice(j, 0, it);
    setForm((f) => ({ ...f, items: next }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.stem_fa.trim() && !form.stem_en.trim()) {
      toast.error(t("ordering.toasts.stem_required"));
      return;
    }
    if (form.items.length < 2) {
      toast.error(t("ordering.toasts.need_two_items"));
      return;
    }
    const payload = {
      kind: "ordering",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: { fa: form.stem_fa || undefined, en: form.stem_en || undefined },
      body: {
        items: form.items.map((it) => ({
          label: { fa: it.fa || undefined, en: it.en || undefined },
        })),
        layout: form.layout,
      },
      scoring: { mode: "position_exact" },
      points: Number(form.points) || 1,
      status: form.status || "published",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");
      toast.success(
        isEditMode
          ? t("ordering.toasts.updated", "سؤال مرتب‌سازی با موفقیت ویرایش شد")
          : t("ordering.toasts.created", "سؤال مرتب‌سازی با موفقیت ساخته شد")
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  return (
    <form onSubmit={submit} className="mb-6 space-y-4">
      {/* Stem */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div dir={isRtl ? "rtl" : "ltr"}>
          <label className="block text-sm font-medium mb-1">
            {t("ordering.stem_fa")}
          </label>
          <CustomEditor
            value={form.stem_fa}
            onEditorChange={(content) => handleEditorChange("stem_fa", content)}
            placeholder={t("ordering.stem_fa_ph")}
            directionality={isRtl ? "rtl" : "ltr"}
            height={150}
            toolbar="undo redo | bold italic underline | bullist numlist"
          />
        </div>
        <div dir="ltr">
          <label className="block text-sm font-medium mb-1">
            {t("ordering.stem_en")}
          </label>
          <CustomEditor
            value={form.stem_en}
            onEditorChange={(content) => handleEditorChange("stem_en", content)}
            placeholder={t("ordering.stem_en_ph")}
            directionality="ltr"
            height={150}
            toolbar="undo redo | bold italic underline | bullist numlist"
          />
        </div>
      </div>

      {/* Layout Selector */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {t("ordering.layout_label")}
        </label>
        <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
          {["top-bottom", "bottom-top", "left-right", "right-left"].map(
            (layout) => (
              <div key={layout} className="flex items-center">
                <input
                  type="radio"
                  id={`layout-${layout}`}
                  name="layout"
                  value={layout}
                  checked={form.layout === layout}
                  onChange={onChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                />
                <label
                  htmlFor={`layout-${layout}`}
                  className="ml-2 text-sm text-gray-700"
                >
                  {t(`ordering.layout_question_answer_${layout}`)}
                </label>
              </div>
            )
          )}
        </div>
      </div>

      {/* Items */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium">
            {t("ordering.items")}
          </label>
          <button
            type="button"
            onClick={addItem}
            className="text-blue-600 text-sm inline-flex items-center gap-1"
          >
            <Plus size={16} />
            {t("ordering.add_item")}
          </button>
        </div>

        <div className="space-y-2">
          {form.items.map((it, idx) => (
            <div key={idx} className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold">
                  {t("ordering.item_n", { n: idx + 1 })}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="p-1 rounded-full text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                  >
                    <ArrowUp size={16} />
                  </button>
                  <button
                    type="button"
                    className="p-1 rounded-full text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                    onClick={() => move(idx, +1)}
                    disabled={idx === form.items.length - 1}
                  >
                    <ArrowDown size={16} />
                  </button>
                  {form.items.length > 2 && (
                    <button
                      type="button"
                      className="p-1 rounded-full text-red-600 hover:bg-red-100"
                      onClick={() => removeItem(idx)}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder={t("ordering.item_fa_ph")}
                  value={it.fa}
                  onChange={(e) => {
                    const next = [...form.items];
                    next[idx].fa = e.target.value;
                    setForm((f) => ({ ...f, items: next }));
                  }}
                />
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder={t("ordering.item_en_ph")}
                  value={it.en}
                  onChange={(e) => {
                    const next = [...form.items];
                    next[idx].en = e.target.value;
                    setForm((f) => ({ ...f, items: next }));
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("ordering.points")}
          </label>
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            value={form.points}
            onChange={onChange}
            name="points"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("ordering.status")}
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.status}
            onChange={onChange}
            name="status"
          >
            <option value="published">{t("common.published")}</option>
            <option value="draft">{t("common.draft")}</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("ordering.tags")}
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("common.tags_csv")}
            value={form.tags}
            onChange={onChange}
            name="tags"
          />
        </div>
      </div>

      <ExplanationField
        valueFa={form.explanation_fa}
        valueEn={form.explanation_en}
        onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
        onChangeEn={(v) => handleEditorChange("explanation_en", v)}
        isRtl={isRtl}
      />

      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded-lg"
      >
        {isEdit ? t("common.update") : t("common.create")}
      </button>
    </form>
  );
}
